import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./styles/global.css";

const root = document.getElementById("root");
if (root === null) {
  throw new Error("Root element not found");
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/* Service worker registration — production only.
   import.meta.env.PROD is false during development (vite dev server).
   This prevents the SW from interfering with hot module replacement. */
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  let updateCheckInterval: number | undefined;

  navigator.serviceWorker
    .register("/sw.js")
    .then((registration: ServiceWorkerRegistration) => {
      /* Check for updates every 15 minutes.
         Financial app — users need timely security and data fixes. */
      if (updateCheckInterval !== undefined) {
        clearInterval(updateCheckInterval);
      }
      updateCheckInterval = window.setInterval(
        () => {
          void registration.update();
        },
        15 * 60 * 1000,
      );
    })
    .catch((error: unknown) => {
      console.error("Service worker registration failed:", error);
    });

  /* Listen for update notifications from the service worker */
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    if (
      event.data !== null &&
      typeof event.data === "object" &&
      "type" in event.data &&
      event.data.type === "sw-updated"
    ) {
      showUpdateBanner();
    }
  });
}

/**
 * Show a non-disruptive update banner at the top of the page.
 * User chooses when to reload — no forced refresh during financial operations.
 * Uses DOM APIs only (no innerHTML) for CSP compatibility.
 */
function showUpdateBanner(): void {
  /* Duplicate banner guard — multiple sw-updated messages must not stack */
  if (document.querySelector(".sw-update-banner") !== null) {
    return;
  }

  const banner = document.createElement("div");
  banner.className = "sw-update-banner";
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");

  const text = document.createElement("span");
  text.textContent = "A new version is available.";

  const refreshBtn = document.createElement("button");
  refreshBtn.className = "sw-update-banner-action";
  refreshBtn.textContent = "Refresh";
  refreshBtn.addEventListener("click", () => {
    window.location.reload();
  });

  const dismissBtn = document.createElement("button");
  dismissBtn.className = "sw-update-banner-dismiss";
  dismissBtn.textContent = "Dismiss";
  dismissBtn.addEventListener("click", () => {
    banner.remove();
  });

  banner.appendChild(text);
  banner.appendChild(refreshBtn);
  banner.appendChild(dismissBtn);
  document.body.appendChild(banner);
}
